import Link from 'next/link';

import clsxm from '@/lib/clsxm';

import { MarkGlyph, OurMark } from './kit';
import type { AlternativesHubProps, HubCell } from './types';
import { SectionHeading, tone } from '../primitives';

/**
 * Every comparison in one table. Wider than a phone, so it scrolls sideways
 * with the feature names pinned.
 */
export default function HubMatrix({
  matrix,
}: Pick<AlternativesHubProps, 'matrix'>) {
  return (
    <section id='at-a-glance' className='py-20 sm:py-24'>
      <div className='mx-auto max-w-7xl px-6 lg:px-8'>
        <SectionHeading
          title='At a glance.'
          tail='The short version of every comparison. Each cell links to its source.'
        />
        <div
          className={clsxm(
            'mt-12 overflow-x-auto rounded-[28px] [scrollbar-width:thin]',
            tone.surface,
            tone.line,
          )}
        >
          <table className='w-full min-w-[66rem] border-collapse text-start'>
            <caption className='sr-only'>
              Easy-Peasy.AI compared with{' '}
              {matrix.columns.map((c) => c.name).join(', ')}
            </caption>
            <thead>
              <tr className='border-b border-[#0f0f28]/[0.07] dark:border-white/[0.08]'>
                <th
                  scope='col'
                  className={clsxm(
                    'sticky start-0 z-10 w-44 bg-white px-5 py-5 text-start text-sm font-medium dark:bg-[#14141f]',
                    tone.quiet,
                  )}
                >
                  Feature
                </th>
                <th
                  scope='col'
                  className='min-w-[9rem] bg-primary-50/70 px-4 py-5 text-start dark:bg-primary-400/[0.08]'
                >
                  <span className='flex items-center gap-2'>
                    <OurMark className='h-6 w-6' />
                    <span
                      className={clsxm(
                        'whitespace-nowrap text-sm font-semibold',
                        tone.ink,
                      )}
                    >
                      Easy-Peasy.AI
                    </span>
                  </span>
                </th>
                {matrix.columns.map((column) => (
                  <th
                    key={column.slug}
                    scope='col'
                    className='min-w-[8rem] px-4 py-5 text-start'
                  >
                    <Link
                      href={`/alternatives/${column.slug}`}
                      className={clsxm(
                        'text-sm font-semibold underline decoration-[#0f0f28]/15 underline-offset-4 transition-colors hover:text-primary-700 hover:decoration-primary-400 dark:decoration-white/20 dark:hover:text-primary-300',
                        tone.ink,
                      )}
                    >
                      {column.name}
                    </Link>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {matrix.rows.map((row, rowIndex) => (
                <tr
                  key={row.key}
                  className='border-b border-[#0f0f28]/[0.06] align-top last:border-b-0 dark:border-white/[0.07]'
                >
                  <th
                    scope='row'
                    className={clsxm(
                      'sticky start-0 z-10 bg-white px-5 py-4 text-start text-sm font-semibold dark:bg-[#14141f]',
                      tone.ink,
                    )}
                  >
                    {row.label}
                  </th>
                  <td className='bg-primary-50/70 px-4 py-4 dark:bg-primary-400/[0.08]'>
                    <MatrixCell cell={matrix.ours[rowIndex]} />
                  </td>
                  {matrix.columns.map((column) => (
                    <td key={column.slug} className='px-4 py-4'>
                      <MatrixCell cell={column.cells[rowIndex]} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className={clsxm('mt-5 max-w-3xl text-sm leading-6', tone.quiet)}>
          Drift isn’t in the table because it is being retired; see its own
          page. Prices are the lowest paid plan billed monthly unless marked.
        </p>
      </div>
    </section>
  );
}

function MatrixCell({ cell }: { cell: HubCell | null }) {
  if (!cell) {
    return <span className={clsxm('text-sm', tone.quiet)}>—</span>;
  }
  return (
    <span className='flex items-start gap-2'>
      {cell.mark && <MarkGlyph mark={cell.mark} className='mt-px' />}
      <span className={clsxm('min-w-0 text-sm leading-5', tone.body)}>
        {cell.text}
        {cell.sources?.map((source) => (
          <a
            key={source.url}
            href={source.url}
            target='_blank'
            rel='nofollow noopener noreferrer'
            title={source.label}
            aria-label={`Source: ${source.label}`}
            className='ms-1 inline-block align-super text-[10px] font-semibold text-primary-600 hover:text-primary-800 dark:text-primary-300'
          >
            ↗
          </a>
        ))}
      </span>
    </span>
  );
}
